'use client';
import './TeacherOverview.css';
import '../../../SkeletonLoader/SkeletonLoader.css';
import { Box, Checkbox, Rating, Typography, Modal, Button } from '@mui/material';
import OneOnOneSessionIcon from '../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import { useState } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { useEffect } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'react-hot-toast';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import { formatTimeFromUTC } from '../../../../../utils/dateUtils';
import { formatCreativeFee } from '../../../../../utils/textUtils';

const formatText = text => {
  if (!text) return '';

  const withSpaces = text.replace(/_/g, ' ');

  return withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#279BE2',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeSessionIcon,
  },
};

// const DayAvailable = {
//   monday: { value: "Mon" },
//   tuesday: { value: "Tue" },
//   wednesday: { value: "Wed" },
//   thursday: { value: "Thu" },
//   friday: { value: "Fri" },
//   saturday: { value: "Sat" },
//   sunday: { value: "Sun" },
// }

// const DayArray = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

const TeacherOverview = () => {
  const [teacherDetails, setTeacherDetails] = useState(null);
  // const [isLoading, setIsLoading] = useState(false)
  const { id } = useParams();
  const [primeStartDate, setPrimeStartDate] = useState(null);
  const [primeEndDate, setPrimeEndDate] = useState(null);
  const [dateError, setDateError] = useState('');
  // Add new state variables for confirmation modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sessionToToggle, setSessionToToggle] = useState(null);
  const [isEnabling, setIsEnabling] = useState(false);
  // Add new state variables for time frame modal
  const [timeFrameModalOpen, setTimeFrameModalOpen] = useState(false);
  const [tempPrimeStartDate, setTempPrimeStartDate] = useState(null);
  const [tempPrimeEndDate, setTempPrimeEndDate] = useState(null);
  const [isEditingTimeFrame, setIsEditingTimeFrame] = useState(false);
  const [isIndefinite, setIsIndefinite] = useState(false);
  const [tempIsIndefinite, setTempIsIndefinite] = useState(false);
  const [pendingPrimeStartDate, setPendingPrimeStartDate] = useState(null);
  const [pendingPrimeEndDate, setPendingPrimeEndDate] = useState(null);
  const [pendingIsIndefinite, setPendingIsIndefinite] = useState(false);
  const [rbacMe, setRbacMe] = useState(null);

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  // Check if user can manage users (required for prime session time frame)
  const canManageUsers = () => {
    const roles = Array.isArray(rbacMe?.roles) ? rbacMe.roles : [];
    return (
      roles.includes('super_admin') || roles.includes('admin') || hasPermission('manage_users')
    );
  };

  const getTeacherDetails = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/admin/teachers/${id}`);
      setTeacherDetails(data);
    } catch (error) {
      console.error('Error fetching teacher details:', error);
    }
  }, [id]);

  const getPrimeSessionTimeFrame = useCallback(async () => {
    if (!id) return;
    // Only fetch if user has permission to manage users
    if (!canManageUsers()) {
      return;
    }
    try {
      const config = {
        _skipErrorToast: true, // Suppress toast for expected 403 errors
      };
      const { data } = await axiosInstance.get(
        `/admin/teachers/prime-session-time-frame/${id}`,
        config
      );
      if (data) {
        setPrimeStartDate(new Date(data.start_date));
        setPrimeEndDate(new Date(data.end_date));
      }
    } catch (error) {
      // Silently handle 403 errors for users without permission
      if (error?.response?.status === 403) {
        console.log('User does not have permission to view prime session time frame');
        return;
      }
      console.error('Error fetching prime session time frame:', error);
    }
  }, [id, rbacMe]);

  useEffect(() => {
    getTeacherDetails();
    // Only fetch prime session time frame if user has permission
    if (rbacMe && canManageUsers()) {
      getPrimeSessionTimeFrame();
    }
  }, [getTeacherDetails, getPrimeSessionTimeFrame, rbacMe]);

  const openConfirmationModal = sessionType => {
    console.log('Opening confirmation modal for:', sessionType);

    const permissionMap = {
      'one-on-one': 'allow_one_on_one_session',
      prime: 'allow_prime_session',
      group: 'allow_group_session',
    };

    const permissionKey = permissionMap[sessionType];
    const currentlyEnabled = teacherDetails?.[permissionKey] || false;
    console.log('Current session enabled state:', currentlyEnabled);

    if (sessionType === 'prime' && !currentlyEnabled) {
      const today = new Date();
      const defaultStartDate = today;
      const defaultEndDate = addDays(today, 14);

      setTempPrimeStartDate(primeStartDate || defaultStartDate);
      setTempPrimeEndDate(primeEndDate || defaultEndDate);
      setTempIsIndefinite(isIndefinite);
      setTimeFrameModalOpen(true);
      return;
    }

    setSessionToToggle(sessionType);
    setIsEnabling(!currentlyEnabled);
    setConfirmModalOpen(true);
  };

  const handlePermissionToggle = async sessionType => {
    console.log('Toggle clicked for:', sessionType);
    console.log(
      'Current value:',
      teacherDetails?.[`allow_${sessionType.replace(/-/g, '_')}_session`]
    );

    if (sessionType === 'one-on-one' && teacherDetails?.allow_one_on_one_session) {
      console.log('Showing toast for one-on-one session');

      try {
        toast.custom(
          t => (
            <div
              className="flex bg-[#FFF4E5] shadow-lg rounded-lg mt-[40px]"
              style={{ zIndex: 9999 }}
            >
              <div className="flex-1 w-full p-4 flex items-center gap-4">
                <InfoIcon style={{ color: '#FFB016' }} />
                <p className="text-sm font-medium text-gray-900">
                  One-on-one sessions cannot be disabled
                </p>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="w-[14px] h-[14px] cursor-pointer rounded-full flex items-center justify-center border border-[#D5DEE3] p-2"
                >
                  <span style={{ color: '#D5DEE3', fontSize: '14px' }}>×</span>
                </button>
              </div>
            </div>
          ),
          {
            duration: 5000,
            position: 'top-center',
          }
        );

        toast.error('One-on-one sessions cannot be disabled', {
          duration: 3000,
          position: 'bottom-center',
        });
      } catch (error) {
        console.error('Error showing toast:', error);
        alert('One-on-one sessions cannot be disabled');
      }
      return;
    }

    openConfirmationModal(sessionType);
  };

  const confirmPermissionChange = async () => {
    try {
      const permissionMap = {
        'one-on-one': 'allow_one_on_one_session',
        prime: 'allow_prime_session',
        group: 'allow_group_session',
      };

      const permissionKey = permissionMap[sessionToToggle];
      const updatedPermissions = {
        allow_one_on_one_session: teacherDetails?.allow_one_on_one_session,
        allow_prime_session: teacherDetails?.allow_prime_session,
        allow_group_session: teacherDetails?.allow_group_session,
        [permissionKey]: !teacherDetails[permissionKey],
        can_use_wallet: teacherDetails?.can_use_wallet ?? true,
        can_use_chat: teacherDetails?.can_use_chat ?? true,
        can_use_bookings: teacherDetails?.can_use_bookings ?? true,
      };

      if (sessionToToggle === 'prime' && !teacherDetails?.allow_prime_session) {
        const startDate = pendingPrimeStartDate || primeStartDate;
        // Fix: Use 31/12/2099 if pendingIsIndefinite is true
        const endDate = pendingIsIndefinite
          ? new Date(2099, 11, 31)
          : pendingPrimeEndDate || primeEndDate;

        updatedPermissions.prime_session_start_date = format(startDate, 'yyyy-MM-dd');
        updatedPermissions.prime_session_end_date = format(endDate, 'yyyy-MM-dd');
      }

      setTeacherDetails(prev => ({
        ...prev,
        ...updatedPermissions,
      }));

      if (
        sessionToToggle === 'prime' &&
        !teacherDetails?.allow_prime_session &&
        pendingPrimeStartDate
      ) {
        setPrimeStartDate(pendingPrimeStartDate);
        // Fix: Use 31/12/2099 if pendingIsIndefinite is true
        setPrimeEndDate(pendingIsIndefinite ? new Date(2099, 11, 31) : pendingPrimeEndDate);
        setIsIndefinite(pendingIsIndefinite);
        setPendingPrimeStartDate(null);
        setPendingPrimeEndDate(null);
        setPendingIsIndefinite(false);
      }

      await axiosInstance.patch(`/admin/teachers/${id}/permissions`, updatedPermissions);
      setDateError('');
      setConfirmModalOpen(false);
    } catch (error) {
      console.error('Error updating permission:', error);
      getTeacherDetails();
      setConfirmModalOpen(false);
    }
  };

  const handleStartDateChange = date => {
    if (!date || !(date instanceof Date) || isNaN(date)) {
      if (isEditingTimeFrame) {
        setTempPrimeStartDate(null);
      } else {
        setTempPrimeStartDate(null);
      }
      return;
    }

    if (isEditingTimeFrame) {
      setTempPrimeStartDate(date);

      const minimumEndDate = addDays(date, 14);

      if (tempPrimeEndDate && !tempIsIndefinite && differenceInDays(tempPrimeEndDate, date) < 14) {
        setTempPrimeEndDate(minimumEndDate);
      }

      setDateError('');
    } else {
      // Updating start date while enabling (not editing)
      setTempPrimeStartDate(date);

      const minimumEndDate = addDays(date, 14);

      if (tempPrimeEndDate && !isIndefinite && differenceInDays(tempPrimeEndDate, date) < 14) {
        setTempPrimeEndDate(minimumEndDate);
      }

      setDateError('');
    }
  };

  const handleEndDateChange = date => {
    if (isEditingTimeFrame) {
      setTempPrimeEndDate(date);
      if (
        tempPrimeStartDate &&
        !tempIsIndefinite &&
        differenceInDays(date, tempPrimeStartDate) < 14
      ) {
        setDateError('There should be at least 2 weeks gap between start and end dates');
      } else {
        setDateError('');
      }
    } else {
      setTempPrimeEndDate(date);
      if (tempPrimeStartDate && !isIndefinite && differenceInDays(date, tempPrimeStartDate) < 14) {
        setDateError('There should be at least 2 weeks gap between start and end dates');
      } else {
        setDateError('');
      }
    }
  };

  const handleIndefiniteToggle = event => {
    const checked = event.target.checked;

    if (isEditingTimeFrame) {
      setTempIsIndefinite(checked);
      if (checked) {
        const indefiniteDate = new Date(2099, 11, 31);
        setTempPrimeEndDate(indefiniteDate);
        setDateError('');
      } else if (tempPrimeStartDate) {
        const newEndDate = addDays(tempPrimeStartDate, 14);
        setTempPrimeEndDate(newEndDate);
      }
    } else {
      // While enabling prime session
      setTempIsIndefinite(checked);
      if (checked) {
        const indefiniteDate = new Date(2099, 11, 31);
        setTempPrimeEndDate(indefiniteDate);
        setDateError('');
      } else if (tempPrimeStartDate) {
        const newEndDate = addDays(tempPrimeStartDate, 14);
        setTempPrimeEndDate(newEndDate);
      }
    }
  };

  const openEditTimeFrameModal = () => {
    const today = new Date();
    const defaultStartDate = today;
    const defaultEndDate = addDays(today, 14);

    setTempPrimeStartDate(primeStartDate || defaultStartDate);
    setTempPrimeEndDate(primeEndDate || defaultEndDate);
    setTempIsIndefinite(isIndefinite);
    setIsEditingTimeFrame(true);
    setTimeFrameModalOpen(true);
  };

  const handleSaveTimeFrame = async () => {
    try {
      console.log('Validating with start date:', tempPrimeStartDate);

      if (
        !tempPrimeStartDate ||
        !(tempPrimeStartDate instanceof Date) ||
        isNaN(tempPrimeStartDate)
      ) {
        setDateError('Please select a start date');
        return;
      }

      if (
        !tempIsIndefinite &&
        (!tempPrimeEndDate || !(tempPrimeEndDate instanceof Date) || isNaN(tempPrimeEndDate))
      ) {
        setDateError('Please select an end date');
        return;
      }

      if (!tempIsIndefinite && differenceInDays(tempPrimeEndDate, tempPrimeStartDate) < 14) {
        setDateError('There should be at least 2 weeks gap between start and end dates');
        return;
      }

      // Fix: Use 31/12/2099 if indefinite is checked
      const endDate = tempIsIndefinite ? new Date(2099, 11, 31) : tempPrimeEndDate;

      if (!isEditingTimeFrame) {
        setPendingPrimeStartDate(tempPrimeStartDate);
        setPendingPrimeEndDate(endDate);
        setPendingIsIndefinite(tempIsIndefinite);
        setDateError('');
        setTimeFrameModalOpen(false);

        setSessionToToggle('prime');
        setIsEnabling(true);
        setConfirmModalOpen(true);
      } else {
        const updatedPermissions = {
          allow_one_on_one_session: teacherDetails?.allow_one_on_one_session,
          allow_prime_session: teacherDetails?.allow_prime_session,
          allow_group_session: teacherDetails?.allow_group_session,
          prime_session_start_date: format(tempPrimeStartDate, 'yyyy-MM-dd'),
          prime_session_end_date: format(endDate, 'yyyy-MM-dd'),
          can_use_wallet: teacherDetails?.can_use_wallet ?? true,
          can_use_chat: teacherDetails?.can_use_chat ?? true,
          can_use_bookings: teacherDetails?.can_use_bookings ?? true,
        };

        setPrimeStartDate(tempPrimeStartDate);
        setPrimeEndDate(endDate);
        setIsIndefinite(tempIsIndefinite);

        await axiosInstance.patch(`/admin/teachers/${id}/permissions`, updatedPermissions);
        setDateError('');
        setTimeFrameModalOpen(false);
        setIsEditingTimeFrame(false);

        toast.success('Prime session time frame updated successfully', {
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error updating time frame:', error);
      toast.error('Failed to update time frame', {
        position: 'top-center',
      });
    }
  };

  const cancelTimeFrameModal = () => {
    setTimeFrameModalOpen(false);
    setDateError('');
    setIsEditingTimeFrame(false);
    setTempIsIndefinite(isIndefinite);
    setPendingPrimeStartDate(null);
    setPendingPrimeEndDate(null);
    setPendingIsIndefinite(false);
  };

  const cancelPermissionChange = () => {
    setConfirmModalOpen(false);
    setSessionToToggle(null);
    setPendingPrimeStartDate(null);
    setPendingPrimeEndDate(null);
    setPendingIsIndefinite(false);
  };

  const formatTime = timeString => {
    return formatTimeFromUTC(timeString);
  };

  const getSessionAvailability = sessionType => {
    if (!teacherDetails?.availability) return null;

    const sessionAvailability = teacherDetails.availability.find(
      item => item.session_type.toLowerCase() === sessionType.toLowerCase()
    );

    return sessionAvailability?.availability || [];
  };

  const getShortDayName = day => {
    const dayMap = {
      Monday: 'Mon',
      Tuesday: 'Tue',
      Wednesday: 'Wed',
      Thursday: 'Thu',
      Friday: 'Fri',
      Saturday: 'Sat',
      Sunday: 'Sun',
    };

    return dayMap[day] || day;
  };

  const SessionBox = ({ type, val, teacherDetails, handlePermissionToggle }) => {
    const permissionMap = {
      'one-on-one': 'allow_one_on_one_session',
      prime: 'allow_prime_session',
      group: 'allow_group_session',
    };

    const isChecked = teacherDetails?.[permissionMap[type]] || false;

    const isDisabled = type === 'one-on-one' && isChecked;

    const checkboxClass = type === 'one-on-one' ? 'one-on-one-checkbox' : '';

    const handleClick = () => {
      console.log('Checkbox clicked for:', type);
      console.log('Is checked:', isChecked);
      console.log('Is disabled:', isDisabled);

      if (isDisabled) return;

      handlePermissionToggle(type);
    };

    const formatDisplayDate = date => {
      if (!date) return '';

      const dateObj = new Date(date);
      if (dateObj.getFullYear() === 2099 && dateObj.getMonth() === 11 && dateObj.getDate() === 30) {
        return 'Indefinite';
      }

      return format(new Date(date), 'dd/MM/yyyy');
    };

    return (
      <Box className="flex flex-col">
        <Box className="flex gap-4 items-center">
          <Checkbox
            checked={isChecked}
            onClick={handleClick}
            color="primary"
            disabled={isDisabled}
            className={checkboxClass}
          />
          <Box className="flex gap-4 rounded-[8px] bg-[#FFF] px-[8px] py-[4px]">
            <img src={SessionTypeColor[val]?.icon || '/placeholder.svg'} alt="Default icon" />
            <Typography className="session-type-text" sx={{ color: SessionTypeColor[val]?.color }}>
              {SessionTypeColor[val]?.value}
            </Typography>
          </Box>
        </Box>

        {type === 'prime' && isChecked && primeStartDate && primeEndDate && (
          <Box className="flex flex-col ml-10 mt-2">
            <Box className="flex items-center gap-4">
              {/* <Typography
                sx={{ color: '#AD8B00', fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500 }}
              >
                <img
                  src={SessionTypeColor['prime']?.icon || '/placeholder.svg'}
                  alt="Prime icon"
                  style={{
                    display: 'inline',
                    marginRight: '4px',
                    width: '16px',
                    height: '16px',
                    verticalAlign: 'text-bottom',
                  }}
                />
                Prime
              </Typography>
              */}
            </Box>
            <Box className="flex gap-8 mt-2">
              <Box className="flex flex-col gap-1">
                <Typography sx={{ color: '#ADADAD', fontFamily: 'Poppins', fontSize: '12px' }}>
                  From
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    width: '140px',
                    height: '36px',
                    backgroundColor: '#FFF',
                    color: '#14171F',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {formatDisplayDate(primeStartDate)}
                </Box>
              </Box>
              <Box className="flex flex-col gap-1">
                <Typography sx={{ color: '#ADADAD', fontFamily: 'Poppins', fontSize: '12px' }}>
                  To
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    width: '140px',
                    height: '36px',
                    backgroundColor: '#FFF',
                    color: '#14171F',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {formatDisplayDate(primeEndDate)}
                </Box>
              </Box>
              <Button
                onClick={openEditTimeFrameModal}
                sx={{
                  minWidth: 'auto',
                  padding: '2px 8px',
                  color: '#0A5EF3',
                }}
              >
                <EditIcon fontSize="small" style={{ alignItems: 'end !important' }} />
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderSessionAvailability = sessionType => {
    const availabilityData = getSessionAvailability(sessionType);

    // Check if there's actual availability data
    if (!availabilityData || availabilityData.length === 0) {
      return (
        <Typography className="text-[16px] font-medium text-gray-600 italic">
          Not scheduled yet
        </Typography>
      );
    }

    // Group time slots
    const timeSlots = {};

    availabilityData.forEach(slot => {
      const timeKey = `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;
      if (!timeSlots[timeKey]) {
        timeSlots[timeKey] = [...slot.days];
      } else {
        timeSlots[timeKey] = [...timeSlots[timeKey], ...slot.days];
      }
    });

    // Render the time slots with their days
    return (
      <Box className="flex flex-col gap-4">
        {Object.entries(timeSlots).map(([timeSlot, days], index) => (
          <Box key={index} className="flex flex-col gap-2">
            <div className="available-date-text">{timeSlot}</div>
            <Box className="flex gap-2">
              {days && days.length > 0 ? (
                days.map((day, dayIndex) => (
                  <Box
                    key={dayIndex}
                    className="flex gap-4 rounded-[8px] bg-[#FFF] px-[8px] py-[4px]"
                  >
                    {getShortDayName(day)}
                  </Box>
                ))
              ) : (
                <Typography className="text-[14px] font-normal text-gray-600 italic">
                  No days selected
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (!teacherDetails) {
    return (
      <Box className="flex flex-col items-start gap-[19px] bg-[#FFF] rounded-[8px] p-4">
        <Box className="flex flex-col gap-4 w-[100%]">
          <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
            {[...Array(4)].map((_, index) => (
              <Box key={index} className="flex flex-col gap-1 w-[15%]">
                <div
                  className="skeleton-text"
                  style={{ width: '80px', height: '14px', marginBottom: '8px' }}
                />
                <div className="skeleton-text" style={{ width: '60px', height: '20px' }} />
              </Box>
            ))}
          </Box>
          <Box className="flex flex-col gap-4 w-[100%]">
            <div
              className="skeleton-text"
              style={{ width: '120px', height: '18px', marginBottom: '12px' }}
            />
            <Box className="flex flex-wrap gap-4">
              {[...Array(3)].map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: '200px',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                  }}
                >
                  <div
                    className="skeleton-text"
                    style={{ width: '100px', height: '14px', marginBottom: '8px' }}
                  />
                  <div
                    className="skeleton-text"
                    style={{
                      width: '80px',
                      height: '20px',
                      marginBottom: '8px',
                    }}
                  />
                  <div className="skeleton-text" style={{ width: '60px', height: '12px' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const getSessionDisplayName = sessionType => {
    const sessionMap = {
      'one-on-one': 'One-On-One',
      group: 'Group',
      prime: 'Prime',
    };
    return sessionMap[sessionType] || sessionType;
  };

  return (
    <Box className="flex flex-col items-start gap-[19px] bg-[#FFF] rounded-[8px]">
      <Box className="flex flex-col gap-4 w-[100%]">
        <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="flex flex-col gap-1 w-[15%]">
            <Typography className="session-history-key-text">Total Hours</Typography>
            <Typography className="session-history-value-text-teacher">
              {teacherDetails?.total_hours_taught} hours
            </Typography>
          </Box>
          <Box className="flex flex-col gap-1 w-[15%]">
            <Typography className="session-history-key-text">Total Sessions</Typography>
            <Typography className="session-history-value-text-teacher">
              {teacherDetails?.total_sessions}
            </Typography>
          </Box>
          <Box className="flex flex-col gap-1 w-[15%]">
            <Typography className="session-history-key-text">Average Rating</Typography>
            <Rating
              name="half-rating-read"
              value={Number(teacherDetails?.rating) || 0}
              precision={0.1}
              readOnly
            />
          </Box>

          <Box className="flex flex-col gap-1 w-[15%]">
            <Typography className="session-history-key-text">Instructor Type</Typography>
            <Typography className="session-history-value-text-teacher">
              {formatText(teacherDetails?.teacher_type)}
            </Typography>
          </Box>
        </Box>
        {teacherDetails?.teacher_type === 'creative' && (
          <>
            <Typography className="availability-text">Fee Settings</Typography>
            <Box className="flex flex-wrap items-center content-center gap-6 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
              <Box className="flex flex-col gap-1 w-[20%]">
                <Typography className="session-history-key-text">Selected Session Fee</Typography>
                <Typography className="session-history-value-text-teacher">
                  {formatCreativeFee(teacherDetails?.fees)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
        <Typography className="availability-text">Availability</Typography>
        <Box className="flex flex-col gap-4 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          {teacherDetails?.allow_one_on_one_session && (
            <Box className="one-on-one-session-container">
              <Box className="flex gap-4 rounded-[8px] bg-[#FFF] px-[8px] py-[4px]">
                <img
                  src={SessionTypeColor['one_on_one']?.icon || '/placeholder.svg'}
                  alt="Default icon"
                />
                <Typography
                  className="session-type-text"
                  sx={{ color: SessionTypeColor['one_on_one']?.color }}
                >
                  {SessionTypeColor['one_on_one']?.value}
                </Typography>
              </Box>
              {renderSessionAvailability('one_on_one')}
            </Box>
          )}
          {teacherDetails?.allow_group_session && (
            <Box className="group-session-container">
              <Box className="flex gap-4 rounded-[8px] bg-[#FFF] px-[8px] py-[4px]">
                <img
                  src={SessionTypeColor['group']?.icon || '/placeholder.svg'}
                  alt="Default icon"
                />
                <Typography
                  className="session-type-text"
                  sx={{ color: SessionTypeColor['group']?.color }}
                >
                  {SessionTypeColor['group']?.value}
                </Typography>
              </Box>
              {renderSessionAvailability('group')}
            </Box>
          )}
          {teacherDetails?.allow_prime_session && (
            <Box className="group-session-container" sx={{ background: '#F0EFFF' }}>
              <Box className="flex gap-4 rounded-[8px] bg-[#FFF] px-[8px] py-[4px]">
                <img
                  src={SessionTypeColor['prime']?.icon || '/placeholder.svg'}
                  alt="Default icon"
                />
                <Typography
                  className="session-type-text"
                  sx={{ color: SessionTypeColor['prime']?.color }}
                >
                  {SessionTypeColor['prime']?.value}
                </Typography>
              </Box>
              {renderSessionAvailability('prime')}
            </Box>
          )}
        </Box>
        <Typography className="availability-text">Type of Sessions</Typography>
        <Box className="flex flex-col gap-4 self-stretch p-3 rounded-md bg-[#FAFAFA] w-[100%]">
          <Box className="session-container">
            <Box className="flex flex-col gap-2">
              <SessionBox
                type="one-on-one"
                val="one_on_one"
                teacherDetails={teacherDetails}
                handlePermissionToggle={handlePermissionToggle}
              />
              <SessionBox
                type="group"
                val="group"
                teacherDetails={teacherDetails}
                handlePermissionToggle={handlePermissionToggle}
              />
              <SessionBox
                type="prime"
                val="prime"
                teacherDetails={teacherDetails}
                handlePermissionToggle={handlePermissionToggle}
              />
            </Box>
          </Box>
        </Box>

        <Modal
          open={timeFrameModalOpen}
          onClose={cancelTimeFrameModal}
          aria-labelledby="time-frame-modal-title"
          aria-describedby="time-frame-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography
              id="time-frame-modal-title"
              variant="h6"
              component="h2"
              sx={{
                fontFamily: 'Poppins',
                fontSize: '18px',
                fontWeight: 500,
                mb: 3,
              }}
            >
              {isEditingTimeFrame
                ? 'Edit Prime Session Time Frame'
                : 'Set Prime Session Time Frame'}
            </Typography>

            <Box className="flex gap-8 mb-4">
              <Box className="flex flex-col gap-2">
                <Typography
                  sx={{
                    color: '#ADADAD',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '100%',
                  }}
                >
                  From
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={isEditingTimeFrame ? tempPrimeStartDate : tempPrimeStartDate}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'DD/MM/YYYY',
                        sx: {
                          width: '180px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#bdbdbd',
                            },
                            '& input': {
                              color: '#14171F',
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              lineHeight: '100%',
                            },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#ADADAD',
                            fontFamily: 'Poppins',
                            fontSize: '12px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '100%',
                            opacity: 1,
                          },
                        },
                      },
                      day: {
                        sx: {
                          '&.Mui-selected': {
                            backgroundColor: '#0A5EF3 !important',
                            color: '#FFFFFF !important',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: '#0A5EF3 !important',
                            },
                            '&:focus': {
                              backgroundColor: '#0A5EF3 !important',
                            },
                          },
                        },
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: '12px',
                          boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                          '& .MuiPickersDay-root': {
                            '&.Mui-selected': {
                              backgroundColor: '#0A5EF3 !important',
                              color: '#FFFFFF !important',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#0A5EF3 !important',
                              },
                            },
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            fontFamily: 'Poppins',
                            fontSize: '12px',
                            color: '#667085',
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#14171F',
                          },
                        },
                      },
                      actionBar: { actions: ['today'] },
                      calendarHeader: {
                        sx: {
                          px: 2,
                          '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                            color: '#14171F',
                          },
                        },
                      },
                      leftArrowButton: { sx: { color: '#14171F' } },
                      rightArrowButton: { sx: { color: '#14171F' } },
                      switchViewButton: { sx: { display: 'none' } },
                    }}
                    format="dd/MM/yyyy"
                  />
                </LocalizationProvider>
              </Box>
              <Box className="flex flex-col gap-2">
                <Typography
                  sx={{
                    color: '#ADADAD',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '100%',
                  }}
                >
                  To
                </Typography>
                {(isEditingTimeFrame && tempIsIndefinite) ||
                (!isEditingTimeFrame && tempIsIndefinite) ? (
                  <Box
                    sx={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      width: '180px',
                      height: '40px',
                      backgroundColor: '#f5f5f5',
                      color: '#14171F',
                      fontFamily: 'Poppins',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Indefinite
                  </Box>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={isEditingTimeFrame ? tempPrimeEndDate : tempPrimeEndDate}
                      onChange={handleEndDateChange}
                      minDate={
                        isEditingTimeFrame && tempPrimeStartDate
                          ? addDays(tempPrimeStartDate, 14)
                          : tempPrimeStartDate
                            ? addDays(tempPrimeStartDate, 14)
                            : undefined
                      }
                      shouldDisableDate={date => {
                        if (!isEditingTimeFrame) return false;
                        if (!tempPrimeStartDate) return false;
                        return differenceInDays(date, tempPrimeStartDate) < 14;
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'DD/MM/YYYY',
                          sx: {
                            width: '180px',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                              '& fieldset': {
                                borderColor: '#e0e0e0',
                              },
                              '&:hover fieldset': {
                                borderColor: '#bdbdbd',
                              },
                              '& input': {
                                color: '#14171F',
                                fontFamily: 'Poppins',
                                fontSize: '12px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '100%',
                              },
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: '#ADADAD',
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 400,
                              lineHeight: '100%',
                              opacity: 1,
                            },
                          },
                        },
                        day: {
                          sx: {
                            '&.Mui-selected': {
                              backgroundColor: '#0A5EF3 !important',
                              color: '#FFFFFF !important',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#0A5EF3 !important',
                              },
                              '&:focus': {
                                backgroundColor: '#0A5EF3 !important',
                              },
                            },
                          },
                        },
                        desktopPaper: {
                          sx: {
                            borderRadius: '12px',
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                            '& .MuiPickersDay-root': {
                              '&.Mui-selected': {
                                backgroundColor: '#0A5EF3 !important',
                                color: '#FFFFFF !important',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#0A5EF3 !important',
                                },
                              },
                            },
                            '& .MuiDayCalendar-weekDayLabel': {
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              color: '#667085',
                            },
                            '& .MuiPickersCalendarHeader-label': {
                              fontFamily: 'Poppins',
                              fontSize: '16px',
                              fontWeight: 500,
                              color: '#14171F',
                            },
                          },
                        },
                        actionBar: { actions: ['today'] },
                        calendarHeader: {
                          sx: {
                            px: 2,
                            '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                              color: '#14171F',
                            },
                          },
                        },
                        leftArrowButton: { sx: { color: '#14171F' } },
                        rightArrowButton: { sx: { color: '#14171F' } },
                        switchViewButton: { sx: { display: 'none' } },
                      }}
                      format="dd/MM/yyyy"
                    />
                  </LocalizationProvider>
                )}
              </Box>
            </Box>

            <Box className="flex items-center mt-4 mb-3 p-2 rounded-md bg-[#F5F7FA] border border-[#E0E0E0]">
              <Checkbox
                checked={isEditingTimeFrame ? tempIsIndefinite : tempIsIndefinite}
                onChange={handleIndefiniteToggle}
                color="primary"
                id="indefinite-checkbox"
                sx={{
                  color: '#0A5EF3',
                  '&.Mui-checked': {
                    color: '#0A5EF3',
                  },
                }}
              />
              <Typography
                component="label"
                htmlFor="indefinite-checkbox"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#14171F',
                  cursor: 'pointer',
                }}
              >
                Indefinite (no end date)
              </Typography>
            </Box>

            {dateError && (
              <Typography className="text-sm text-red-500 mb-3">{dateError}</Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                onClick={cancelTimeFrameModal}
                sx={{
                  fontFamily: 'Poppins',
                  textTransform: 'none',
                  borderRadius: '8px',
                  color: '#0A5EF3',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTimeFrame}
                variant="contained"
                sx={{
                  fontFamily: 'Poppins',
                  textTransform: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#0A5EF3',
                  '&:hover': {
                    backgroundColor: '#0A5EF3',
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Confirmation Modal */}
        <Modal
          open={confirmModalOpen}
          onClose={cancelPermissionChange}
          aria-labelledby="confirmation-modal-title"
          aria-describedby="confirmation-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography
              id="confirmation-modal-title"
              variant="h6"
              component="h2"
              sx={{
                fontFamily: 'Poppins',
                fontSize: '18px',
                fontWeight: 500,
                mb: 2,
              }}
            >
              Confirm {isEnabling ? 'Enable' : 'Disable'} Session
            </Typography>
            <Typography
              id="confirmation-modal-description"
              sx={{
                mb: 3,
                fontFamily: 'Poppins',
                fontSize: '14px',
              }}
            >
              Are you sure you want to {isEnabling ? 'enable' : 'disable'}{' '}
              {getSessionDisplayName(sessionToToggle)} sessions for this instructor?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={cancelPermissionChange}
                sx={{
                  fontFamily: 'Poppins',
                  textTransform: 'none',
                  borderRadius: '8px',
                  color: '#0A5EF3',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmPermissionChange}
                variant="contained"
                sx={{
                  fontFamily: 'Poppins',
                  textTransform: 'none',
                  borderRadius: '8px',
                  backgroundColor: isEnabling ? '#0A5EF3' : '#F44336',
                  '&:hover': {
                    backgroundColor: isEnabling ? '#0A5EF3' : '#d32f2f',
                  },
                }}
              >
                {isEnabling ? 'Enable' : 'Disable'}
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

export default TeacherOverview;
